import { React, useState } from "react";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import PropTypes from "prop-types";
import {
  Divider,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  Autocomplete,
  IconButton,
  Box,
  Typography,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import { styled } from "@mui/material/styles";
import { getAiPrompt } from "../../../services/userServices";
import Button from "@mui/material/Button";

const optionPromptType = [
  { name: "Summarize", typeof: "Summarize", color: "#98FF98" },
  { name: "Summarize with bullets", typeof: "Bullets", color: "#6495ED" },
  { name: "Re-write for clarity", typeof: "Clarity", color: "#008080" },
  { name: "Re-write for positivity", typeof: "Positivity", color: "#FFD700" },
  { name: "Create SMART Goals", typeof: "Goals", color: "#00FFFF" },
];

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",

  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

export default function AIPrompt(props) {
  const { promptOpen, handleClose, currentNotes, fieldMaxLenght, setAiNotes } =
    props;
  const [loading, setLoading] = useState(false);
  const [promptType, setPromptType] = useState({
    name: "Summarize",
    typeof: "Summarize",
    color: "#98FF98",
  });
  const [notes, setNotes] = useState(currentNotes || "");

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const showSnack = (message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const handleTextAreaChange = (e, setStateMethod) => {
    setStateMethod(e.target.value);
  };

  const Generate = async () => {
    if (promptType.typeof === null || promptType.typeof === "") {
      showSnack("Please select a prompt type.", "warning");
    } else if (notes === null || notes === "") {
      showSnack("Please enter some valid text.", "warning");
    } else {
      setLoading(true);

      let payload = preparePromptPayload();

      const response = await getAiPrompt(payload);
      if (response?.message === "Success") {
        setNotes(response.data);
        setAiNotes(response.data);
      } else if (response?.message === "Error") {
        showSnack("Error in generating prompt", "error");
      }
    }
    setLoading(false);
  };

  const preparePromptPayload = () => ({
    text: notes || "",
    mode: promptType?.typeof,
    maxLength: 2000,
  });

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Dialog
        maxWidth="md"
        fullWidth
        open={promptOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          promptOpen(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontSize: 18,
              fontWeight: "bold",
              color: "#3299FF",
              letterSpacing: "0.10em",
            }}
          >
            <AutoAwesomeIcon
              sx={{
                color: "#3299FF",
              }}
            />
            AI Writing Assistant
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => handleClose(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Autocomplete
                disablePortal
                freeSolo
                openOnFocus
                sx={{ marginTop: 1 }}
                value={promptType}
                options={optionPromptType}
                getOptionLabel={(option) => option.name}
                onChange={(event, newValue) => setPromptType(newValue)}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    display="flex"
                    alignItems="center"
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        backgroundColor: option.color,
                        marginRight: 1,
                      }}
                    />
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Prompt Type"
                    label={
                      <span>
                        Prompt Type
                        <Box component={"span"} sx={{ color: "red" }}>
                          *
                        </Box>
                      </span>
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          {params.inputProps.value && (
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                backgroundColor: optionPromptType.find(
                                  (option) =>
                                    option.name === params.inputProps.value,
                                )?.color,
                                marginLeft: 1,
                              }}
                            />
                          )}
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                name="Notes"
                value={notes}
                rows={10}
                label="Write with AI"
                InputLabelProps={{
                  sx: {
                    fontWeight: "bold",
                  },
                }}
                placeholder={`Max ${fieldMaxLenght} characters allowed`}
                helperText={`${notes ? notes.length : 0}/${fieldMaxLenght}`}
                inputProps={{ maxLength: fieldMaxLenght }}
                onChange={(event) => handleTextAreaChange(event, setNotes)}
                sx={{
                  transition: "box-shadow 0.3s ease",
                  boxShadow: notes ? "0 0 8px rgba(50, 153, 255, 1)" : "none",
                  color: "#3299FF",
                }}
                FormHelperTextProps={{
                  sx: {
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <CustomButton
                  variant="contained"
                  onClick={Generate}
                  startIcon={<AutoAwesomeIcon />}
                >
                  Generate
                </CustomButton>
                <CustomButton
                  variant="contained"
                  onClick={() => handleClose(true)}
                  startIcon={<ThumbUpAltIcon />}
                >
                  Accept
                </CustomButton>

                <CustomOutlinedButton
                  variant="outlined"
                  size="small"
                  onClick={() => handleClose(false)}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </CustomOutlinedButton>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
}

AIPrompt.propTypes = {
  promptOpen: PropTypes.func,
  handleClose: PropTypes.func,
  currentNotes: PropTypes.string,
  fieldMaxLenght: PropTypes.number,
  setAiNotes: PropTypes.func,
};
